import { useState, useEffect, useRef } from 'react';
import siteData from '../data/data.json';
import logo from '../images/nopsa_logo.png';

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBadge, setShowBadge] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: (
        <span>
          Hi there! I'm NopsaBot 🤖. Would you like to learn more about our latest product{' '}
          <a
            href="https://www.cloudidentitymesh.com/"
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              e.preventDefault();
              respondIdentityMesh();
            }}
          >
            Cloud Identity Mesh,
          </a>{' '}
           our{' '}
          <a
            href="#healthcheck"
            onClick={(e) => {
              e.preventDefault();
              respondHealthCheck();
            }
          }>
            Cloud Health Check
          </a>{' '}
          service or maybe detailed info about our
          <a
          href="#services"
            onClick={(e) => {
              e.preventDefault();
              respondServicesList();
            }}
          > Services
          </a>
          ? I can also assist with anything else you need. If you need more information you can{' '}
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              handleContactClick();
            }}
            >contact us directly here</a>.
        </span>
      ),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const quickReplies = [];

  const respondIdentityMesh = () => {
    setMessages((msgs) => [
      ...msgs,
      {
        role: 'assistant',
        content: (
          <span>
            Cloud Identity Mesh unifies identity, policy, and network trust across clouds and apps—zero-trust by default with centralized policy, least privilege, and automated posture checks. Learn more at{' '}
            <a href="https://www.cloudidentitymesh.com/" target="_blank" rel="noreferrer">
              cloudidentitymesh.com
            </a>.
          </span>
        ),
      },
    ]);
    setIsTyping(false);
  };

  const respondHealthCheck = () => {
    setMessages((msgs) => [
      ...msgs,
      {
        role: 'assistant',
        content: (
          <span>
            Our Cloud Health Check is a fast, agentless security review with actionable findings. You can dive into it here:{' '}
            <a href="#healthcheck" onClick={() => setIsOpen(false)}>
              Cloud Health Check
            </a>.
          </span>
        ),
      },
    ]);
    setIsTyping(false);
  };

  const handleContactClick = () => {
    setIsOpen(false);
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const respondServiceDetail = (service) => {
    if (!service) return;
    setMessages((msgs) => [
      ...msgs,
      {
        role: 'assistant',
        content: (
          <span>
            <strong>{service.name}</strong>: {service.text} If you'd like to discuss this service, please{' '}
            <a href="#contact" onClick={handleContactClick}>
              contact us
            </a>.
          </span>
        ),
      },
    ]);
    setIsTyping(false);
  };

  const respondServicesList = () => {
    const services = siteData?.Services || [];
    setMessages((msgs) => [
      ...msgs,
      {
        role: 'assistant',
        content: (
          <div>
            <p>Here are our services. Click any for a quick overview:</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {services.map((s) => (
                <li key={s.name}>
                  <button className="service-link" onClick={() => respondServiceDetail(s)}>
                    {s.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ),
      },
    ]);
    setIsTyping(false);
  };

  // Future enhancement: load persisted conversation context
  useEffect(() => {
    // Example: retrieve messages from localStorage or backend with auth
  }, []);

  // Scroll to latest message
  const messagesEndRef = useRef(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleQuickReply = (text) => {
    if (text.toLowerCase().includes('identity mesh')) {
      respondIdentityMesh();
      return;
    }
    if (text.toLowerCase().includes('health')) {
      respondHealthCheck();
      return;
    }
    if (text.toLowerCase().includes('services')) {
      respondServicesList();
      return;
    }
    if (text.toLowerCase().includes('contact')) {
      handleContactClick();
      return;
    }
    sendMessage(text);
  };

  const sendMessage = async (messageInput) => {
    const text = messageInput !== undefined ? messageInput : input;
    if (!text.trim()) return;
    setMessages((msgs) => [...msgs, { role: 'user', content: text }]);
    setInput('');
    setIsTyping(true);

    const normalize = (str) => str.toLowerCase().replace(/[^\w\s]/g, '');
    const lowered = normalize(text.trim());
    const services = siteData?.Services || [];
    const healthCheck = siteData?.HealthCheck;

    if (lowered.includes('identity') && lowered.includes('mesh')) {
      respondIdentityMesh();
      return;
    }

    if (lowered.includes('health') && lowered.includes('check')) {
      respondHealthCheck();
      return;
    }

    if (lowered.includes('services')) {
      const servicesResponse = (
        <div>
          <p>Nopsa Ventures offers the following services:</p>
          <ul>
            {services.map((s) => (
              <li key={s.name}>
                <button className="service-link" onClick={() => sendMessage(s.name)}>
                  {s.name}
                </button>
              </li>
            ))}
          </ul>
          <p>Do you need more details on specific service or feature?</p>
        </div>
      );
      setMessages((msgs) => [...msgs, { role: 'assistant', content: servicesResponse }]);
      setIsTyping(false);
      return;
    }

    if (lowered.includes('contact')) {
      const contactReply = (
        <span>
          We'd love to hear from you! <a href="#contact" onClick={handleContactClick}>Contact us</a>.
        </span>
      );
      setMessages((msgs) => [...msgs, { role: 'assistant', content: contactReply }]);

      setIsTyping(false);
      return;
    }

    if (lowered.includes('health') && lowered.includes('check')) {
      const intro =
        healthCheck?.intro ||
        'Our Cloud Health Check offers a fast, agentless security review.';
      const details =
        "You'll meet with our experts, connect your cloud for a no-agent scan, and receive an actionable report in less than 48 hours.";
      const healthReply = (
        <span>
          {intro} {details} To schedule your Cloud Health Check, visit the{' '}
          <a href="#healthcheck" onClick={() => setIsOpen(false)}>
            Cloud Health Check section
          </a>.
        </span>
      );
      setMessages((msgs) => [...msgs, { role: 'assistant', content: healthReply }]);
      setIsTyping(false);
      return;
    }

    const serviceMatch = services.find((s) =>
      lowered.includes(normalize(s.name))
    );
    if (serviceMatch) {
      try {
        const prompt = `Service: ${serviceMatch.name}\nDescription: ${serviceMatch.text}\nWrite a brief, customer-friendly summary of this service.`;
        const response = await fetch('https://ai-worker.nopsa-ventures.workers.dev/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ message: prompt })
        });

        const contentType = response.headers.get('content-type') || '';
        let data = null;
        if (contentType.includes('application/json')) {
          try {
            data = await response.json();
          } catch (err) {
            // fall through to error handling below
          }
        } else {
          await response.text(); // consume body to free stream
        }

        if (!response.ok || !data || data.error) {
          const errMsg = data?.error || response.statusText || 'Invalid response from server';
          setMessages((msgs) => [...msgs, { role: 'assistant', content: 'Error: ' + errMsg }]);
        } else {
          const reply = data?.reply || 'No response';
          const finalReply = (
            <span>
              {reply} If you'd like to explore this service further, please{' '}
              <a href="#contact" onClick={handleContactClick}>contact us</a>.
            </span>
          );
          setMessages((msgs) => [...msgs, { role: 'assistant', content: finalReply }]);
        }
      } catch (error) {
        setMessages((msgs) => [...msgs, { role: 'assistant', content: 'Error: ' + error.message }]);
      } finally {
        setIsTyping(false);
      }
      return;
    }

    try {
      const response = await fetch('https://ai-worker.nopsa-ventures.workers.dev/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: text })
      });

      const contentType = response.headers.get('content-type') || '';
      let data = null;
      if (contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (err) {
          // fall through to error handling below
        }
      } else {
        await response.text(); // consume body to free stream
      }

      if (!response.ok || !data || data.error) {
        const errMsg = data?.error || response.statusText || 'Invalid response from server';
        setMessages((msgs) => [...msgs, { role: 'assistant', content: 'Error: ' + errMsg }]);
      } else {
        const reply = data?.reply || 'No response';
        setMessages((msgs) => [...msgs, { role: 'assistant', content: reply }]);
      }
    } catch (error) {
      setMessages((msgs) => [...msgs, { role: 'assistant', content: 'Error: ' + error.message }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const toggleChat = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (showBadge) setShowBadge(false);
  };

  return (
    <div className={`chatbot-container ${isOpen ? 'open' : ''}`}>
      {!isOpen && (
        <button className="chatbot-toggle" onClick={toggleChat} aria-label="Open chatbot">
          <span className="chatbot-emoji" role="img" aria-label="Chatbot">🤖</span>
          {showBadge && <span className="chatbot-badge">1</span>}
        </button>
      )}
      {isOpen && (
        <div className="chatbot-window">
          <button className="chatbot-close" onClick={toggleChat} aria-label="Close chatbot">×</button>
          {quickReplies.length > 0 && (
            <div className="quick-replies">
              {quickReplies.map((qr) => (
                <button key={qr} onClick={() => handleQuickReply(qr)}>
                  {qr}
                </button>
              ))}
            </div>
          )}
          <div className="messages">
            {messages.map((m, idx) => (
              <div key={idx} className={`message ${m.role}`}>
                {m.role === 'assistant' && <img src={logo} alt="NopsaBot" className="avatar" />}
                <div className="bubble">{m.content}</div>
              </div>
            ))}
            {isTyping && (
              <div className="message assistant">
                <img src={logo} alt="NopsaBot" className="avatar" />
                <div className="bubble typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="input-row">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
            />
            <button className="send" onClick={() => sendMessage()}>Send</button>
          </div>
        </div>
      )}
    </div>
  );
};
